import {
  Children,
  type FC,
  type ReactElement,
  type ReactNode,
  cloneElement,
  isValidElement,
  useMemo,
} from 'react';

/**
 * Hook that recursively searches through React children to find all elements that match a specific type.
 * Supports both string types (HTML elements) and component types with displayName.
 * Memoizes results to prevent expensive re-computations when children or type haven't changed.
 *
 * @param {ReactNode} children - The React children to search through.
 * @param {string} type - The type to search for (HTML tag name or component displayName).
 * @returns {Array<ReactElement<any>>} Memoized array of matching React elements with keys assigned.
 */
export const useChildrenOfType = (children: ReactNode, type: string): Array<ReactElement<any>> => {
  return useMemo(() => {
    const result: Array<ReactElement<any>> = [];

    if (!children) return result;

    const isMatchingType = (element: ReactElement): boolean => {
      const { type: elementType } = element;

      // Fast path for string types (HTML elements)
      if (typeof elementType === 'string') {
        return elementType === type;
      }

      // Check displayName for components (with null check)
      if (typeof elementType === 'object' || typeof elementType === 'function') {
        return (elementType as FC)?.displayName === type;
      }

      return false;
    };

    // Use a single counter for stable key generation across the entire tree
    let keyCounter = 0;

    const loopThroughChildren = (children: ReactNode, depth = 0): void => {
      // Prevent runaway recursion (reasonable limit for UI components)
      if (depth > 10) return;

      Children.forEach(children, (child) => {
        if (!isValidElement(child)) return;

        // Check if this element matches our target type
        if (isMatchingType(child)) {
          // Only clone if no key exists, otherwise preserve original key
          const elementToAdd = child.key
            ? child
            : cloneElement(child, { key: `auto-${keyCounter++}` });

          result.push(elementToAdd as ReactElement<any>);
        }

        // Recurse into children only if they exist
        if (child.props?.children) {
          loopThroughChildren(child.props.children, depth + 1);
        }
      });
    };

    loopThroughChildren(children);

    return result;
  }, [children, type]);
};
